import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { FileText, MessageSquareReply, MessageSquareText, PanelRightClose, PanelRightOpen } from 'lucide-react'
import {
  useAddNote,
  useConversation,
  useDeleteNote,
  useSendReply,
  useSetStatus,
  useTimeline,
  useTyping,
  type Conversation,
  type TimelineItem,
} from '../api/inbox'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { autoReplyLabel } from '../api/autoReplies'
import { fillCannedReply, matchCannedReplies, useCannedReplies, type CannedReply } from '../api/cannedReplies'
import { BackToList, WindowBadge } from '../pages/InboxPage'
import { TemplateSender } from './TemplateSender'
import { CustomerPanel } from './CustomerPanel'
import { useMediaQuery } from '../lib/media'
import { useShortcuts } from '../lib/shortcuts'
import { bodyText, deliveryLabel, fullTime, newId, useNow, windowState } from '../lib/format'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

type Role = 'owner' | 'admin' | 'agent' | 'viewer'

export function ConversationPane({ id }: { id: string }) {
  const conv = useConversation(id)
  const me = useMe()

  if (conv.isPending || me.isPending) {
    return (
      <div role="status" aria-label="Loading the conversation" className="flex flex-col gap-3 p-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
    )
  }
  if (conv.isError) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <BackToList />
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(conv.error as RequestFailed).message}</p>
      </div>
    )
  }
  if (me.isError) {
    return <p role="alert" className="m-4 rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(me.error as RequestFailed).message}</p>
  }

  return <Loaded conv={conv.data} role={me.data.user.role} meId={me.data.user.id} />
}

function Loaded({ conv, role, meId }: { conv: Conversation; role: Role; meId: string }) {
  const now = useNow()
  const w = windowState(conv.serviceWindowExpiresAt, now)
  const canWrite = role !== 'viewer'
  // Wide screens show the customer panel as a third column, open unless
  // hidden; narrower ones open it as a sheet over the conversation.
  const wide = useMediaQuery('(min-width: 1280px)')
  const [column, setColumn] = useState(true)
  const [sheet, setSheet] = useState(false)
  const panelShown = wide ? column : sheet
  const togglePanel = () => (wide ? setColumn((o) => !o) : setSheet((o) => !o))
  const navigate = useNavigate()
  useShortcuts({ i: togglePanel, Escape: () => { if (!wide) navigate('/inbox') } })

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b bg-card px-4 py-3">
          <BackToList />
          <div className="min-w-0 flex-[1_1_160px]">
            <h2 className="truncate text-base font-semibold">{conv.contact.name}</h2>
            <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {conv.contact.waId ? `+${conv.contact.waId}` : 'Number not shared'}
              {conv.assignee ? `, assigned to ${conv.assignee.id === meId ? 'you' : conv.assignee.name}` : ', unassigned'}
            </p>
          </div>
          <WindowBadge w={w} />
          {canWrite && <StatusButton conv={conv} />}
          <Button
            variant="outline"
            size="icon"
            aria-label={panelShown ? 'Hide customer details' : 'Show customer details'}
            aria-pressed={panelShown}
            aria-controls={wide ? 'customer-panel' : undefined}
            onClick={togglePanel}
          >
            {panelShown && wide ? <PanelRightClose aria-hidden="true" /> : <PanelRightOpen aria-hidden="true" />}
          </Button>
        </header>
        <Timeline id={conv.id} role={role} meId={meId} />
        {canWrite ? (
          <Composer id={conv.id} customer={conv.contact.name} windowOpen={w.open} closedReason={w.open ? undefined : w.reason} />
        ) : (
          <p className="border-t bg-card px-4 py-3 text-sm text-muted-foreground">Viewers can read conversations but cannot reply.</p>
        )}
      </div>
      {wide && column && (
        <aside id="customer-panel" aria-label="Customer details" className="w-80 shrink-0 overflow-y-auto border-l bg-card">
          <CustomerPanel conv={conv} role={role} meId={meId} />
        </aside>
      )}
      {!wide && (
        <Sheet open={sheet} onOpenChange={setSheet}>
          <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-sm">
            <SheetHeader className="pb-0">
              <SheetTitle>Customer details</SheetTitle>
              <SheetDescription className="sr-only">Assignment, tags and consent for {conv.contact.name}</SheetDescription>
            </SheetHeader>
            <CustomerPanel conv={conv} role={role} meId={meId} />
          </SheetContent>
        </Sheet>
      )}
    </div>
  )
}

function StatusButton({ conv }: { conv: Conversation }) {
  const status = useSetStatus(conv.id)
  return (
    <>
      <Button variant="outline" disabled={status.isPending} onClick={() => status.mutate(conv.status === 'open' ? 'closed' : 'open')}>
        {conv.status === 'open' ? 'Close' : 'Reopen'}
      </Button>
      {status.error && <p role="alert" className="basis-full text-sm text-destructive">{(status.error as RequestFailed).message}</p>}
    </>
  )
}

function Timeline({ id, role, meId }: { id: string; role: Role; meId: string }) {
  const tl = useTimeline(id)
  const del = useDeleteNote(id)
  if (tl.isPending) return <p role="status" className="flex-1 p-4 text-sm text-muted-foreground">Loading messages</p>
  if (tl.isError) {
    return (
      <div className="flex-1 p-4">
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(tl.error as RequestFailed).message}</p>
      </div>
    )
  }

  // Pages arrive newest first; the reader wants oldest at the top.
  const items: TimelineItem[] = tl.data.pages.flatMap((p) => p.items).reverse()
  if (items.length === 0) return <p className="flex-1 p-6 text-center text-sm text-muted-foreground">No messages in this conversation yet.</p>
  const admin = role === 'owner' || role === 'admin'

  return (
    <ol aria-label="Messages" className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
      {tl.hasNextPage && (
        <li className="self-center">
          <Button variant="outline" size="sm" disabled={tl.isFetchingNextPage} onClick={() => void tl.fetchNextPage()}>
            {tl.isFetchingNextPage ? 'Loading' : 'Show older messages'}
          </Button>
        </li>
      )}
      {items.map((it) => {
        if (it.kind === 'note' && it.note) {
          const n = it.note
          const canDelete = role !== 'viewer' && (admin || n.author.id === meId)
          return (
            <li key={`n-${n.id}`} className="max-w-[min(640px,95%)] self-center rounded-lg border border-dashed border-attention-text bg-note px-3 py-2 text-sm">
              <span className="font-semibold text-attention-text">{n.author.name}, team note: </span>
              <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{n.body}</span>
              <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                {fullTime(n.createdAt)} · Customer does not see this
                {canDelete && (
                  <Button variant="link" size="xs" className="h-auto p-0 text-xs" disabled={del.isPending} onClick={() => del.mutate(n.id)}>
                    Delete
                  </Button>
                )}
              </span>
            </li>
          )
        }
        if (!it.message) return null
        const m = it.message
        const outbound = m.direction === 'outbound'
        const failed = m.status === 'failed'
        return (
          <li
            key={`m-${m.id}`}
            className={cn(
              'max-w-[min(560px,85%)] px-3 py-2 text-[15px]',
              outbound ? 'self-end rounded-[14px_4px_14px_14px] bg-bubble-out' : 'self-start rounded-[4px_14px_14px_14px] border bg-bubble-in',
              failed && 'border border-destructive bg-card',
            )}
          >
            {m.templateName && (
              <span className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <FileText className="size-3" aria-hidden="true" />
                Template: {m.templateName}
              </span>
            )}
            {m.autoReply && (
              <span className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <MessageSquareReply className="size-3" aria-hidden="true" />
                Auto-reply: {autoReplyLabel[m.autoReply]}
              </span>
            )}
            <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{bodyText(m)}</p>
            <span className={cn('mt-0.5 block text-xs text-muted-foreground', failed && 'font-semibold text-destructive')}>
              {fullTime(m.sentAt)}
              {outbound && ` · ${deliveryLabel(m)}`}
            </span>
          </li>
        )
      })}
      {del.error && <li role="alert" className="self-center text-sm text-destructive">{(del.error as RequestFailed).message}</li>}
    </ol>
  )
}

function Composer({
  id,
  customer,
  windowOpen,
  closedReason,
}: {
  id: string
  customer: string
  windowOpen: boolean
  closedReason: string | undefined
}) {
  const [mode, setMode] = useState<'reply' | 'template' | 'note'>('reply')
  // A draft per tab: text typed as a note never turns into a reply by
  // switching tabs, and switching back keeps it.
  const [drafts, setDrafts] = useState({ reply: '', note: '' })
  const draftMode = mode === 'note' ? 'note' : 'reply'
  const text = drafts[draftMode]
  const setText = (v: string) => setDrafts((d) => ({ ...d, [draftMode]: v }))
  // One idempotency key per message typed; kept across a failed retry.
  const key = useRef(newId())
  const box = useRef<HTMLTextAreaElement>(null)
  const send = useSendReply(id)
  const note = useAddNote(id)
  const typing = useTyping(id)
  // r, t and n pick a tab; the text box takes focus once it has rendered.
  const [focusSoon, setFocusSoon] = useState(false)
  const choose = (m: 'reply' | 'template' | 'note') => {
    setMode(m)
    setPickerOpen(false)
    if (m !== 'template') setFocusSoon(true)
  }
  useShortcuts({ r: () => choose('reply'), t: () => choose('template'), n: () => choose('note') })
  useEffect(() => {
    if (!focusSoon) return
    box.current?.focus()
    setFocusSoon(false)
  }, [focusSoon])
  const busy = send.isPending || note.isPending
  const replyBlocked = mode === 'reply' && !windowOpen
  const error = (mode === 'reply' ? send.error : mode === 'note' ? note.error : null) as RequestFailed | null

  // The canned reply picker opens when a reply starts with "/" and nothing
  // else has been typed yet, or from the button. Picking fills the draft;
  // nothing is sent until Send.
  const canned = useCannedReplies()
  const [pickerOpen, setPickerOpen] = useState(false)
  const slash = mode === 'reply' ? /^\/(\S*)$/.exec(text) : null
  const showPicker = mode === 'reply' && !replyBlocked && (pickerOpen || slash !== null)
  const matches = showPicker ? matchCannedReplies(canned.data ?? [], slash?.[1] ?? '') : []
  const [active, setActive] = useState(0)
  const activeIndex = Math.min(active, Math.max(matches.length - 1, 0))

  const pick = (c: CannedReply) => {
    setText(fillCannedReply(c.body, customer))
    setPickerOpen(false)
    setActive(0)
    box.current?.focus()
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    // The template tab has its own Send button. Enter in one of its fields
    // submits this form; it must never send or save the reply draft.
    if (mode === 'template') return
    const body = text.trim()
    if (!body || busy) return
    const sentMode = draftMode
    const done = () => {
      setDrafts((d) => ({ ...d, [sentMode]: '' }))
      if (sentMode === 'reply') key.current = newId()
    }
    if (mode === 'reply') send.mutate({ text: body, clientMessageId: key.current }, { onSuccess: done })
    else note.mutate(body, { onSuccess: done })
  }
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const current = matches[activeIndex]
    if (showPicker && current) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((activeIndex + 1) % matches.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((activeIndex - 1 + matches.length) % matches.length); return }
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); pick(current); return }
      if (e.key === 'Tab') { e.preventDefault(); pick(current); return }
    }
    if (e.key === 'Escape' && showPicker) { e.preventDefault(); setPickerOpen(false); if (slash) setText(''); return }
    if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur(); return }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit()
  }

  return (
    <form className="flex flex-col gap-2 border-t bg-card px-4 py-3" onSubmit={submit}>
      <Tabs value={mode} onValueChange={(v) => { setMode(v === 'note' ? 'note' : v === 'template' ? 'template' : 'reply'); setPickerOpen(false) }}>
        <TabsList>
          <TabsTrigger value="reply">Reply</TabsTrigger>
          <TabsTrigger value="template">Template</TabsTrigger>
          <TabsTrigger value="note">Team note</TabsTrigger>
        </TabsList>
      </Tabs>
      {mode === 'template' ? (
        <TemplateSender conversationId={id} customer={customer} />
      ) : replyBlocked ? (
        <div className="flex flex-col gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
          <p>{closedReason} An approved template can still reach them.</p>
          <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => setMode('template')}>
            Choose a template
          </Button>
        </div>
      ) : (
        <>
          {showPicker && (
            <CannedPicker
              state={canned.isPending ? 'loading' : canned.isError ? 'error' : (canned.data?.length ?? 0) === 0 ? 'none' : matches.length === 0 ? 'nomatch' : 'list'}
              matches={matches}
              activeIndex={activeIndex}
              onPick={pick}
              onHover={setActive}
            />
          )}
          <Label htmlFor="composer" className="sr-only">{mode === 'reply' ? 'Reply to the customer' : 'Note for your team'}</Label>
          <Textarea
            id="composer"
            ref={box}
            rows={3}
            maxLength={mode === 'reply' ? 4096 : 4000}
            placeholder={mode === 'reply' ? 'Type a reply to the customer, or / for a canned reply' : 'Only your team sees notes'}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setActive(0)
              // Shows "typing..." to the customer; never for a team note.
              if (mode === 'reply' && e.target.value.trim() && !e.target.value.startsWith('/')) typing()
            }}
            onKeyDown={onKeyDown}
            aria-expanded={mode === 'reply' ? showPicker : undefined}
            aria-controls={showPicker ? 'canned-picker' : undefined}
            className={cn('resize-none text-[15px]', mode === 'note' && 'border-dashed bg-note')}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="flex-[1_1_200px] text-xs text-muted-foreground">
              {mode === 'reply' ? 'Sent as a service message. ' : ''}Ctrl and Enter also sends.
            </span>
            {mode === 'reply' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={pickerOpen}
                onClick={() => { setPickerOpen((o) => !o); setActive(0); box.current?.focus() }}
              >
                <MessageSquareText className="size-4" aria-hidden="true" />
                Canned
              </Button>
            )}
            <Button type="submit" disabled={busy || !text.trim()}>
              {busy ? 'Saving' : mode === 'reply' ? `Send to ${customer}` : 'Save note (team only)'}
            </Button>
          </div>
        </>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </form>
  )
}

function CannedPicker({
  state,
  matches,
  activeIndex,
  onPick,
  onHover,
}: {
  state: 'loading' | 'error' | 'none' | 'nomatch' | 'list'
  matches: CannedReply[]
  activeIndex: number
  onPick: (c: CannedReply) => void
  onHover: (i: number) => void
}) {
  const note = (text: ReactNode) => <p className="px-3 py-2 text-sm text-muted-foreground">{text}</p>
  return (
    <div id="canned-picker" className="max-h-56 overflow-y-auto rounded-lg border bg-popover shadow-sm">
      {state === 'loading' && note('Loading canned replies')}
      {state === 'error' && note('Canned replies could not be loaded. Type your reply instead.')}
      {state === 'none' && note(<>No canned replies yet. Owners and admins add them under <Link to="/settings/canned-replies" className="underline">Canned replies</Link>.</>)}
      {state === 'nomatch' && note('No canned reply starts with that. Keep typing or press Escape.')}
      {state === 'list' && (
        <ul role="listbox" aria-label="Canned replies">
          {matches.map((c, i) => (
            <li
              key={c.id}
              role="option"
              aria-selected={i === activeIndex}
              className={cn('cursor-pointer px-3 py-2', i === activeIndex && 'bg-accent')}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => { e.preventDefault(); onPick(c) }}
            >
              <span className="text-sm font-semibold">/{c.shortcut}</span>
              <p className="line-clamp-2 text-xs text-muted-foreground">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
